import React, { useEffect, useState } from "react";
import { TextField, Button, Switch, FormControlLabel, FormControl, Grid, Box, InputAdornment, Paper, Card, CardContent, Typography, IconButton, Tooltip } from "@mui/material";
import { Pie } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    ArcElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
    ChartOptions,

} from "chart.js";
import { useAuth, useSnackbar, useLoading, useNavigation } from "../../Context/Contexts";
import { ReportFilterViewModel, ListQuery, ReportAccountSummaryViewModel, ProgressResultViewModel } from "../../Types/ExportTypes";
import CenteredCard from "../../components/Card/CenteredCardLayout";
import ReportHeader from "../../components/Report/ReportHeader";
import CancelIcon from "@mui/icons-material/Cancel";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import useCachedListQuery from "../../hooks/useCachedListQuery";
import { useTranslation } from "react-i18next";
import DownloadIcon from '@mui/icons-material/GridOn';
import LoadingSection from "../../components/LoadingSection";
import ResponsivePagination from "../../components/ResponsivePagination";

ChartJS.register(CategoryScale, LinearScale, ArcElement, Title, ChartTooltip, Legend);

const AccountSummaryReportPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
    const { showLoading, hideLoading } = useLoading();
    const { authService } = useAuth();
    const { showSnackbar } = useSnackbar();

    const { goBack } = useNavigation();
    const [filter, setFilter] = useState<ReportFilterViewModel>({});
    const [searchFilter, setSearchFilter] = useState<string>("");
    const [query, setQuery] = useState<ListQuery>({
        page: 0,
        pageSize: 10,
        filter: "",
        filters: [],
    });
    const [linesLoading, setLinesLoading] = useState(false);
    const [items, setItems] = useState<ReportAccountSummaryViewModel[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [TotalGraphSum, setTotalGraphSum] = useState(0);
/*    const [totalGraphQty, setTotalGraphQty] = useState(0);*/
    const [initialized, setInitialized] = useState(false);

    const { t } = useTranslation();

    const getGraphData = (itemCollection?: ReportAccountSummaryViewModel[]) => {
        const topItems = [...itemCollection ?? items].sort((a, b) => (b.amount ?? 0) - (a.amount ?? 0)).slice(0, 100);

        return {
            labels: topItems.map(item => item.accountNoDesc + " - " + item.accountDescription),
            datasets: [
                {
                    label: t("common.amount") ,
                    data: topItems.map(item => item.amount),
                    backgroundColor: topItems.map(() => "#" + Math.floor(Math.random() * 16777215).toString(16)), // Random colors
                    borderWidth: 1,
                },
            ],
        };
    };
    const [graphData, setGraphData] = useState(getGraphData);


    const options: ChartOptions<"pie"> = {
        responsive: true,
        plugins: {
            legend: { position: "bottom" },
            title: { display: true, text: t("common.amount") },

        },
    };


    const fetchDocuments = async (initializedOverLoad? : boolean |null) => {
        showLoading();
        setLinesLoading(true);  
        try {
            const response = await authService.post<{ items: ReportAccountSummaryViewModel[], count: number, topResult: ReportAccountSummaryViewModel[] }>(
                `/reportApi/GetAccountSummary`,
                query
            );
            setItems(prev => {

                if (initializedOverLoad === false || !initialized) {

                

                    var loadGraphData = getGraphData(response.topResult);
                    setGraphData(loadGraphData);


                    setInitialized(true);

                }



                setTotalCount(response.count);
                return response.items; // ✅ Return the modified array
            });


        } catch (error) {
            console.error("Failed to fetch documents:", error);
        } finally {
            hideLoading();
            setLinesLoading(false);
        }
    };

    useEffect(() => {
        fetchDocuments();
    }, [query]);

    useEffect(() => {
        const newTotal = graphData.datasets[0].data.reduce((sum, value) => (sum ?? 0) + (value ?? 0), 0) ?? 0;
        const elementCount = graphData.datasets[0].data.length ?? 0;
        setTotalGraphSum(newTotal);

    }, [graphData]);


    const generateReport = async (filter: ReportFilterViewModel) => {
        setFilter(filter);
        showLoading();
        try {
            const response = await authService.post<{ progressId: number }>(
                `/reportApi/GenerateAccountSummary`,
                filter
            );
            await pollStatus(response.progressId);
        } catch (error) {
            console.error("Failed to fetch documents:", error);
        } finally {
            hideLoading();
        }
    };


    const pollStatus = async (progressId: number) => {
        const interval = 5000;
        let attempts = 100;

        while (attempts > 0) {
            try {
                const response = await authService.get<ProgressResultViewModel>(`/progressApi/GetProgress?progressId=${progressId}`);

                if (!response) throw new Error("Failed to fetch status");
                if (response.done) {
                    setInitialized((prev) => {
                        fetchDocuments(false);
                        return false;
                    })
                    break;
                }

            } catch (error) {
                console.error("Error fetching status:", error);
                showSnackbar(t("dialog.fetchStatusError"), "error");
                return;
            }

            attempts--;
            await new Promise((resolve) => setTimeout(resolve, interval));
        }
    };
    const handleSearchSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        setQuery({ ...query, filter: searchFilter, page: 0 });
    };

    const handlePageChange = (newPage: number) => {
        setQuery({ ...query, page: newPage });
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setQuery({ ...query, pageSize: newPageSize, page: 0 });
    };



    const handleExcel = async () => {
        try {
            const response = await authService.post<Blob>(
                "/reportApi/GetAccountSummaryExcel",
                {},
                "blob"
            );


            const url = window.URL.createObjectURL(response);
            const a = document.createElement("a");
            a.href = url;
            a.download = "AccountSummary.xlsx";
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Failed to download Excel report", error);
        }
    };




    return (
        <>
            <CenteredCard title={t("common.accountSummary")} isLoading={false}>
                <ReportHeader filter={filter} onGenerate={generateReport} skipFilters={["advanced"]} />
                {items.length > 0 && (
                    <>
                        <Box sx={{ mt: 4, display: "flex", justifyContent: "center", alignItems: "center", height: "70vh" }}>
                            <Pie data={graphData} options={options} />
                        </Box>
                        
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Typography variant="h6" sx={{ fontWeight: "bold", textAlign: "center" }}>
                                {t("common.totalSum")}: {TotalGraphSum?.toLocaleString()} LCY
                            </Typography>


                        </Box>

                    </>
                )}
                <Paper sx={{ padding: 2 }}>
                    {/* Search Field */}
                    <Box
                        component="form"
                        onSubmit={handleSearchSubmit}
                        sx={{
                            display: "flex",
                            gap: 2,
                            marginBottom: 2,
                        }}
                    >
                        <TextField
                            label={t("common.search")}
                            variant="outlined"
                            fullWidth
                            onChange={(event) => setSearchFilter(event.target.value)}
                            value={searchFilter}

                        />
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"

                        >
                            {t("common.search")}
                        </Button>
                    </Box>
                    {/* Card Layout for Items */}
                    <LoadingSection loading={linesLoading}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Tooltip title={t("help.downloadExcel")}>
                                <Button
                                    variant="contained"
                                    color="success"
                                    fullWidth
                                    onClick={handleExcel}
                                    startIcon={<DownloadIcon />}
                                >
                                    Excel
                                </Button>
                            </Tooltip>
                        </Grid>
                        {items.map((item) => (
                            <Grid item xs={12} key={item.accountNoDesc}>
                                <Grid container spacing={1} sx={{
                                    p: 1.5,
                                    border: '1px solid #ccc',
                                    borderRadius: 1,
                                    cursor: "pointer",
                                    transition: "all 0.2s ease-in-out",
                                    ':hover': {
                                        backgroundColor: '#f9f9f9'
                                    }
                                }}>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1" fontWeight="bold">{item.accountNoDesc}</Typography>
                                    </Grid>
   

                                    <Grid item xs={6} md={4}>
                                        <Typography variant="body2"> {t("common.accountNo")}:</Typography>
                                        <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                            {item.accountNoDesc}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={6} md={4}>
                                        <Typography variant="body2"> {t("common.description")}:</Typography>
                                        <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                            {item.accountDescription}
                                        </Typography>
                                    </Grid>


                                    <Grid item xs={6}>
                                        <Card variant="outlined" sx={{ p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                            <Typography variant="body2"> {t("common.debit")}:</Typography>
                                            <Typography variant="body2" fontWeight="bold" >
                                                {item.debit?.toLocaleString()}
                                            </Typography>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={6}>
                                        <Card variant="outlined" sx={{ p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{t("common.credit")}:</Typography>
                                            <Typography variant="body2" fontWeight="bold" sx={{ wordBreak: 'break-word' }}>
                                                {item.credit?.toLocaleString()}
                                            </Typography>
                                        </Card>
                                    </Grid>


                                    <Grid item xs={12}>
                                        <Card variant="outlined" sx={{ p: 1, backgroundColor: '#f5f5f5', borderRadius: 1, display: "flex", justifyContent: "center", alignItems: "center", }}>
                                            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{t("common.amount")}:</Typography>
                                            <Typography variant="body2" fontWeight="bold" sx={{ wordBreak: 'break-word'   }}>
                                                {item.amount?.toLocaleString()}
                                            </Typography>
                                        </Card>
                                    </Grid>


                                </Grid>
                            </Grid>

                        ))}
                        {items.length === 0 && (
                            <Grid item xs={12}>
                                <Typography align="center">{t("common.noItemsFound")}</Typography>
                            </Grid>
                        )}
                    </Grid>
                    </LoadingSection>
                    <ResponsivePagination

                        page={query.page}

                        pageSize={query.pageSize}

                        totalCount={totalCount}

                        onPageChange={handlePageChange}

                    />
                </Paper>
            </CenteredCard>


        </>
    );
};

export default AccountSummaryReportPage;